import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useSettings } from "../../lib/useSettings";
import {
  LayoutDashboard, Building2, BadgeEuro, Sparkles, FileCheck2, Globe2,
  Palette, Bell, Rocket, ArrowLeft, LogOut, Menu, X, Gauge, Wand2, Download,
  Phone, Mail, MapPin, Settings as SettingsIcon, GitCommitHorizontal, FileEdit, Tag, Globe,
} from "lucide-react";

/**
 * Accès Agence — layout LIGHT mode (navy/sky/blanc) Sprint E.
 * Branding personnalisable via settings.agency_* (logo, couleurs, signature).
 * Login dédié /agency/login si pas auth.
 */
const SECTIONS = [
  {
    label: "Pilotage",
    links: [
      { to: "/agency/dashboard", label: "Cockpit", icon: Gauge },
      { to: "/agency/sites", label: "Sites clients", icon: Globe2 },
      { to: "/agency/domains", label: "Domaines", icon: Globe },
      { to: "/agency/alerts", label: "Alertes", icon: Bell },
      { to: "/agency/cockpit", label: "Pilotage produit", icon: GitCommitHorizontal },
      { to: "/agency/cockpit-update", label: "Mise à jour COCKPIT", icon: Download },
    ],
  },
  {
    label: "Clients & Facturation",
    links: [
      { to: "/agency/clients", label: "Fiches clients", icon: Building2 },
      { to: "/agency/onboarding", label: "Demandes onboarding", icon: Rocket },
      { to: "/agency/drafts", label: "Brouillons onboarding", icon: FileEdit },
      { to: "/agency/pricing", label: "Forfaits", icon: BadgeEuro },
      { to: "/agency/promo-codes", label: "Codes promo", icon: Tag },
      { to: "/agency/widgets", label: "Catalogue widgets", icon: Sparkles },
      { to: "/agency/cgv", label: "CGV & contrats", icon: FileCheck2 },
    ],
  },
  {
    label: "Design & Contenu",
    links: [
      { to: "/agency/theme-generator", label: "Générateur IA (Prompt+URL)", icon: Wand2 },
      { to: "/agency/content-scraper", label: "Scraper de contenu", icon: Download },
      { to: "/agency/studio", label: "Studio chat IA", icon: Sparkles },
      { to: "/agency/presets", label: "Presets secteur", icon: Palette },
    ],
  },
  {
    label: "Paramètres",
    links: [
      { to: "/agency/settings", label: "Branding & signature", icon: SettingsIcon },
    ],
  },
];

export default function AgencyLayout() {
  const { user, logout } = useAuth();
  const { settings: s } = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  if (!user) return <Navigate to="/agency/login" replace />;
  if (user.role !== "admin") return <Navigate to="/agency/login" replace />;
  if (s && s.is_hub !== true && s.is_hub !== undefined) return <Navigate to="/admin" replace />;

  const NAVY = s?.agency_navy_color || "#0F2744";
  const SKY = s?.agency_sky_color || "#38BDF8";

  return (
    <div
      className="min-h-screen flex bg-slate-50 text-slate-900"
      data-testid="agency-layout"
      style={{ "--agency-navy": NAVY, "--agency-sky": SKY }}
    >
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-slate-900/40 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar — light */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-white border-r border-slate-200 z-40 transition-transform overflow-y-auto shadow-sm ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="flex flex-col h-full">
          {/* Header — agency brand */}
          <div className="p-5 border-b border-slate-200 flex items-start gap-3" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY}E6 100%)` }}>
            {s?.agency_logo_url ? (
              <img src={s.agency_logo_url} alt={s.agency_brand_name || "Agence"} className="w-10 h-10 rounded-lg object-cover bg-white shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: SKY }}>
                <Rocket className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: SKY }}>
                {s?.agency_tagline || "Accès Agence"}
              </div>
              <div className="font-bold text-white truncate">{s?.agency_brand_name || s?.company_name || "Agence MSC"}</div>
              <div className="text-[10px] text-white/60">COCKPIT · v{s?.version || "1.3.0"}</div>
            </div>
            <button
              type="button"
              className="lg:hidden text-white/80 hover:text-white"
              onClick={() => setSidebarOpen(false)}
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav sections */}
          <nav className="flex-1 p-3 space-y-5">
            {SECTIONS.map(sec => (
              <div key={sec.label}>
                <div className="flex items-center gap-2 px-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SKY, boxShadow: `0 0 6px ${SKY}80` }} />
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: NAVY }}>{sec.label}</div>
                </div>
                <ul className="space-y-0.5">
                  {sec.links.map(l => (
                    <li key={l.to}>
                      <NavLink
                        to={l.to}
                        className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${isActive ? "bg-slate-100 font-semibold" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`}
                        style={({ isActive }) => isActive ? { borderLeft: `3px solid ${SKY}`, paddingLeft: "9px", color: NAVY } : {}}
                        data-testid={`agency-nav-${l.to.split("/").pop()}`}
                      >
                        <l.icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{l.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Signature footer — agency coordinates */}
          {(s?.agency_signature_email || s?.agency_signature_phone || s?.agency_signature_address) && (
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50 text-xs space-y-1" data-testid="agency-signature-block">
              <div className="font-bold" style={{ color: NAVY }}>{s.agency_brand_name || "Agence MSC"}</div>
              {s.agency_signature_address && (
                <div className="flex items-start gap-1.5 text-slate-500">
                  <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                  <span className="leading-tight">{s.agency_signature_address}</span>
                </div>
              )}
              {s.agency_signature_phone && (
                <a href={`tel:${s.agency_signature_phone}`} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900">
                  <Phone className="w-3 h-3" /> {s.agency_signature_phone}
                </a>
              )}
              {s.agency_signature_email && (
                <a href={`mailto:${s.agency_signature_email}`} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900">
                  <Mail className="w-3 h-3" /> {s.agency_signature_email}
                </a>
              )}
            </div>
          )}

          {/* Footer actions */}
          <div className="p-3 border-t border-slate-200 space-y-1">
            <button
              type="button"
              onClick={() => { sessionStorage.setItem("force_admin", "1"); nav("/admin"); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition"
              data-testid="agency-back-to-admin"
            >
              <ArrowLeft className="w-4 h-4" />
              Back-office de ce site
            </button>
            <button
              type="button"
              onClick={() => { logout(); nav("/agency/login"); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition"
              data-testid="agency-logout"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-3 h-14 bg-white border-b border-slate-200 shadow-sm">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-sm font-bold" style={{ color: NAVY }}>{s?.agency_tagline || "ACCÈS AGENCE"}</div>
          <div className="w-10" />
        </div>

        <div className="agency-scope min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
